import React, { useMemo, useState } from 'react';

// helpers
import styled from 'styled-components';
import useTranslation from 'hooks/useTranslation';
import { userAPI } from 'api/profile/userAPI';
import { StateModel } from 'redux/reducers';
import { setUserTags } from 'redux/actions/auth';
import { colorsTheme } from 'resources/theme/styled/colors';
import { onboardingAPI } from 'api/onboarding/onboardingAPI';
import { OnboardingStatusModel } from 'typings/onboarding/onboarding';
import { useDispatch, useSelector } from 'react-redux';
import { ONBOARDING_ADMINISTRATION_RULES_INTRODUCTION_SUBMITTED } from 'constants/userTags';
import ErrorHandlerService, {
  ErrorFromServer,
} from 'services/error-handler/service';

// components
import Summary from './Summary';
import Introduction from './Introduction';
import ApprovalRules from './ApprovalRules';
import ApprovalGroups from './ApprovalGroups';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import { CardWrapper } from 'components/Additional/CardWrapper';
import { ReactComponent as CheckIcon } from 'resources/icons/remix-icons/check-line.svg';
import {
  Text,
  IconSVG,
  Divider,
  AsyncButton,
  Col,
  Row,
  SectionIntro,
} from '@ui';

interface IProps {
  onboardingData: OnboardingStatusModel;
}

type StepKeys =
  | 'introduction'
  | 'approval-groups'
  | 'approval-rules'
  | 'summary';

const AdministrationRules = ({ onboardingData }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userTags = useSelector<StateModel, string[]>(
    (store) => store.auth.profileData?.tags || [],
  );
  const [stepStatuses, setStepStatuses] = useState<Record<StepKeys, boolean>>({
    introduction: isCompleted('introduction'),
    'approval-groups': isCompleted('approval-groups'),
    'approval-rules': isCompleted('approval-rules'),
    summary: isCompleted('summary'),
  });

  const [selectedStep, setSelectedStep] = useState<StepKeys>(getInitialStep());

  const steps: {
    key: StepKeys;
    title: React.ReactNode;
    component: React.ReactNode;
  }[] = [
    {
      key: 'introduction',
      title: (
        <DivAlignCenter>
          {t('administration.administration_rules.introduction.title')}
          {stepStatuses['introduction'] ? (
            <StyledIconSVG
              component={CheckIcon}
              color={colorsTheme.colorPrimary}
            />
          ) : null}
        </DivAlignCenter>
      ),
      component: <Introduction />,
    },

    {
      key: 'approval-groups',

      title: (
        <DivAlignCenter>
          {t('administration.administration_rules.approval_groups.title')}
          {stepStatuses['approval-groups'] ? (
            <StyledIconSVG
              component={CheckIcon}
              color={colorsTheme.colorPrimary}
            />
          ) : null}
        </DivAlignCenter>
      ),
      component: (
        <ApprovalGroups
          onAction={() => handleTabsDataChange('approval-groups')}
        />
      ),
    },

    {
      key: 'approval-rules',
      title: (
        <DivAlignCenter>
          {t('administration.administration_rules.approval_rules.title')}
          {stepStatuses['approval-rules'] ? (
            <StyledIconSVG
              component={CheckIcon}
              color={colorsTheme.colorPrimary}
            />
          ) : null}
        </DivAlignCenter>
      ),
      component: (
        <ApprovalRules
          onAction={() => handleTabsDataChange('approval-rules')}
        />
      ),
    },

    {
      key: 'summary',
      title: (
        <DivAlignCenter>
          {t('administration.transaction_rules.summary.title')}
          {stepStatuses['summary'] ? (
            <StyledIconSVG
              component={CheckIcon}
              color={colorsTheme.colorPrimary}
            />
          ) : null}
        </DivAlignCenter>
      ),
      component: <Summary />,
    },
  ] as const;

  const content = useMemo(() => {
    const step = steps.find((e) => e.key === selectedStep);
    return step ? step.component : null;
  }, [selectedStep]);

  function handleTabsDataChange(step: StepKeys) {
    switch (step) {
      case 'approval-groups':
        {
          if (stepStatuses['approval-groups']) {
            setStepStatuses((prev) => ({
              ...prev,
              'approval-groups': false,
              'approval-rules': false,
              summary: false,
            }));
          }
        }
        break;

      case 'approval-rules':
        {
          if (stepStatuses['approval-rules']) {
            setStepStatuses((prev) => ({
              ...prev,
              'approval-rules': false,
              summary: false,
            }));
          }
        }
        break;
    }
  }

  function getInitialStep(): StepKeys {
    if (!isCompleted('introduction')) {
      return 'introduction';
    }

    if (!isCompleted('approval-groups')) {
      return 'approval-groups';
    }

    if (!isCompleted('approval-rules')) {
      return 'approval-rules';
    }

    return 'summary';
  }

  function isCompleted(step: StepKeys) {
    switch (step) {
      case 'introduction':
        return userTags.includes(
          ONBOARDING_ADMINISTRATION_RULES_INTRODUCTION_SUBMITTED,
        );

      case 'approval-groups':
        return onboardingData.approvalConfigurationCompletedSteps
          .accountManagementApprovalGroups;

      case 'approval-rules':
        return onboardingData.approvalConfigurationCompletedSteps
          .accountManagementApprovalRules;

      case 'summary':
        return onboardingData.approvalConfigurationCompletedSteps
          .accountManagementApprovalRules;
    }
  }

  const canSelectStep = (step: StepKeys) => {
    switch (step) {
      case 'introduction':
        return true;

      case 'approval-groups':
        return stepStatuses['introduction'];

      case 'approval-rules':
        return stepStatuses['approval-groups'];

      case 'summary':
        return stepStatuses['approval-rules'];
    }
  };

  const handleNext = async () => {
    switch (selectedStep) {
      case 'introduction': {
        await userAPI.setUserTags([
          ONBOARDING_ADMINISTRATION_RULES_INTRODUCTION_SUBMITTED,
        ]);
        dispatch(
          setUserTags([
            ...userTags,
            ONBOARDING_ADMINISTRATION_RULES_INTRODUCTION_SUBMITTED,
          ]),
        );
        setStepStatuses((prev) => ({ ...prev, introduction: true }));
        setSelectedStep('approval-groups');
        break;
      }

      case 'approval-groups': {
        await onboardingAPI.submitAccountManagementApprovalGroupsStep();
        setStepStatuses((prev) => ({ ...prev, 'approval-groups': true }));
        setSelectedStep('approval-rules');
        break;
      }

      case 'approval-rules': {
        try {
          await onboardingAPI.submitAccountManagementApprovalRulesStep();
          setStepStatuses((prev) => ({
            ...prev,
            'approval-rules': true,
            summary: true,
          }));
          setSelectedStep('summary');
        } catch (error) {
          const errorCode = ErrorHandlerService.getErrorCodeFromError(
            error as ErrorFromServer,
          );

          switch (errorCode) {
            case '1025008':
              {
                ErrorHandlerService.displayErrorMessage(
                  t(
                    'administration.administration_rules.approval_rules.invalid_approval_rule',
                  ),
                );
              }
              break;

            default: {
              ErrorHandlerService.handleError(error as ErrorFromServer);
            }
          }
        }
        break;
      }
    }
  };

  return (
    <CardWrapper>
      <Wrapper>
        <div>
          <SectionIntro
            title={
              <DivAlignCenter>
                {t('administration.administration_rules.title')}
              </DivAlignCenter>
            }
          />
          <Row wrap={false}>
            <Col span={19}>
              <Text gutterBottom>
                {t('administration.administration_rules.description')}
              </Text>
              {content}
            </Col>
            <Col span={1}>
              <DividerWrapper>
                <StyledDivider type="vertical" />
              </DividerWrapper>
            </Col>
            <Col span={4}>
              {steps.map((e) => (
                <StyledText
                  key={e.key}
                  disabled={!canSelectStep(e.key)}
                  active={selectedStep == e.key}
                  onClick={() => canSelectStep(e.key) && setSelectedStep(e.key)}
                >
                  {e.title}
                </StyledText>
              ))}
            </Col>
          </Row>
        </div>
        <div>
          <HideIfDisabledForm>
            {selectedStep !== 'summary' ? (
              <StyledAsyncButton size="middle" onClick={handleNext}>
                {t('next', { ns: 'common' })}
              </StyledAsyncButton>
            ) : null}
          </HideIfDisabledForm>
        </div>
      </Wrapper>
    </CardWrapper>
  );
};

const Wrapper = styled.div`
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const DividerWrapper = styled.div`
  display: flex;
  height: 100%;
  justify-content: center;
`;

const StyledDivider = styled(Divider)`
  height: 100%;
  border-color: rgba(66, 81, 91, 0.7);
`;

const StyledText = styled(Text)<{ active?: boolean; disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  margin-bottom: ${({ theme }) => theme.marginXXs};
  transition: ${({ theme }) => theme.defaultTransition};

  ${({ active }) => `color: ${active ? colorsTheme.colorPrimary : ''}`};

  &:hover {
    color: ${({ theme }) => theme.colorPrimary};
  }
`;

const StyledIconSVG = styled(IconSVG)`
  margin-left: ${({ theme }) => theme.marginXXs};
`;

const StyledAsyncButton = styled(AsyncButton)`
  margin-top: ${({ theme }) => theme.marginSm};
`;

export default AdministrationRules;
